import { createSelector } from "reselect";
import { DEVICES } from "../constants/device.constants";
import { initialState } from "../reducers/device.reducer";
import { ADMIN_PAGE } from "../../containers/AdminPage/constants";
import { fromJS, List } from "immutable";
import { mergeV4IntoV3 } from "../../utils/deviceMerge";
import {
  collectChangedSettings,
  generateDefaultValues,
} from "../../containers/DevConV4/utils";

const selectState = (state) => state[DEVICES] || initialState;
const selectAdminState = (state) => state[ADMIN_PAGE] || fromJS({});

export const selectDevices = () =>
  createSelector([selectState, selectAdminState], (state, adminState) => {
    let devices = state.get("data").toList();
    if (!devices?.size) {
      devices = List(adminState.get("devices"));
    }

    const v4Devices = state.get("v4OnlyDevices") || List();
    if (v4Devices.size) {
      const v3ByUuid = new Map();
      const v3ById = new Map();
      const v3ByUniqueId = new Map();
      devices.forEach((d) => {
        if (d.uuid) v3ByUuid.set(d.uuid, d);
        if (d.id) v3ById.set(d.id, d);
        if (d.uniqueId) v3ByUniqueId.set(d.uniqueId, d);
      });

      // ─── PREFER-V3-DEDUP (revertable) ─────────────────────────────────────
      // Prefer the V3 row when a device exists in both V3 and V4: keep the V3
      // row (with its own V3 status/location; only the V4 uuid/owner is carried
      // over so V4 ops resolve) and hide the V4 duplicate. Only append V4
      // devices with NO matching V3 row.
      // To revert to the previous V4-preferred behavior, replace the block below
      // (down to the `if (v4ToAppend.length)`) with the original:
      //
      //   const v3IdsToDrop = new Set();
      //   const v4ToAppend = [];
      //   v4Devices.forEach((d) => {
      //     const matchedV3 = (d.uuid && v3ByUuid.get(d.uuid)) || (d.id && v3ById.get(d.id)) || (d.uniqueId && v3ByUniqueId.get(d.uniqueId)) || null;
      //     if (!matchedV3) v4ToAppend.push(d);
      //     else if (d.isEmerit) { v3IdsToDrop.add(matchedV3.id); v4ToAppend.push(d.set("legacyId", matchedV3.id)); }
      //   });
      //   if (v3IdsToDrop.size) devices = devices.filter((d) => !v3IdsToDrop.has(d.id));
      //   if (v4ToAppend.length) devices = devices.concat(v4ToAppend);
      // ──────────────────────────────────────────────────────────────────────
      const v3Overlays = new Map();
      const v4ToAppend = [];
      v4Devices.forEach((d) => {
        const matchedV3 =
          (d.uuid && v3ByUuid.get(d.uuid)) ||
          (d.id && v3ById.get(d.id)) ||
          (d.uniqueId && v3ByUniqueId.get(d.uniqueId)) ||
          null;
        if (!matchedV3) {
          v4ToAppend.push(d);
        } else if (d.isEmerit) {
          v3Overlays.set(matchedV3.id, mergeV4IntoV3(matchedV3, d));
        }
      });

      if (v3Overlays.size) {
        devices = devices.map((d) => v3Overlays.get(d.id) || d);
      }
      if (v4ToAppend.length) {
        devices = devices.concat(v4ToAppend);
      }
    }

    const trackSingleDeviceId = state.get("trackSingleDeviceId");
    return devices
      .filter((d) => !trackSingleDeviceId || d.id === trackSingleDeviceId)
      .sortBy((d) => d.accountName + d.name);
  });
export const selectDevicesFetched = () =>
  createSelector(selectState, (state) => state.get("fetched"));
export const selectV4OnlyDevices = () =>
  createSelector(selectState, (state) => state.get("v4OnlyDevices") || List());
export const selectSelectedDevice = () =>
  createSelector(selectState, (state) => state.get("selectedDevice"));
export const selectDevConV4 = () =>
  createSelector(selectState, (state) => state.get("devConV4"));
// Unsaved-change count for the device open in DevCon (memoized on its settings).
export const selectDevConV4ChangeCount = () =>
  createSelector(
    (state) => selectState(state).get("devConV4"),
    (settings) =>
      settings
        ? collectChangedSettings(settings, generateDefaultValues(settings)).size
        : 0
  );
export const selectDevConOpen = () =>
  createSelector(selectState, (state) => state.get("devConOpen"));
export const selectEmeritV4Statuses = () =>
  createSelector(selectState, (state) => state.get("emeritV4Statuses"));
export const selectV4ArcDeviceIds = () =>
  createSelector(selectState, (state) => state.get("v4ArcDeviceIds"));
export const selectV4ArcLinkChecks = () =>
  createSelector(selectState, (state) => state.get("v4ArcLinkChecks"));
export const selectDeviceById = (deviceId) =>
  createSelector([selectState, selectAdminState], (state, adminState) => {
    let devices = state.get("data").toList();
    if (!devices?.size) {
      devices = List(adminState.get("devices"));
    }

    // ─── PREFER-V3-DEDUP (revertable) ───────────────────────────────────────
    // Prefer the V3 row on overlap (matching uuid -> id -> uniqueId), carrying
    // only the V4 uuid/owner; only append V4 devices with no V3 row.
    // To revert, restore the original id-only union below and drop the uuid match
    // in the final `find`:
    //
    //   const existingIds = new Set();
    //   devices.forEach((d) => { if (d.id) existingIds.add(d.id); });
    //   const newV4 = v4OnlyDevices.filter((d) => !existingIds.has(d.id));
    //   devices = devices.concat(newV4);
    //   ... return devices.find((d) => d.id?.toString() === deviceId);
    // ────────────────────────────────────────────────────────────────────────
    const v4OnlyDevices = state.get("v4OnlyDevices") || List();
    if (v4OnlyDevices.size) {
      const v3ByUuid = new Map();
      const v3ById = new Map();
      const v3ByUniqueId = new Map();
      devices.forEach((d) => {
        if (d.uuid) v3ByUuid.set(d.uuid, d);
        if (d.id) v3ById.set(d.id, d);
        if (d.uniqueId) v3ByUniqueId.set(d.uniqueId, d);
      });

      const v3Overlays = new Map();
      const newV4 = [];
      v4OnlyDevices.forEach((d) => {
        const matchedV3 =
          (d.uuid && v3ByUuid.get(d.uuid)) ||
          (d.id && v3ById.get(d.id)) ||
          (d.uniqueId && v3ByUniqueId.get(d.uniqueId)) ||
          null;
        if (!matchedV3) {
          newV4.push(d);
        } else if (d.isEmerit) {
          v3Overlays.set(matchedV3.id, mergeV4IntoV3(matchedV3, d));
        }
      });
      if (v3Overlays.size) {
        devices = devices.map((d) => v3Overlays.get(d.id) || d);
      }
      if (newV4.length) {
        devices = devices.concat(newV4);
      }
    }

    return devices.find(
      (d) => d.id?.toString() === deviceId || d.uuid === deviceId
    );
  });
